import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import DefaultLayout from "layout/defaultLayout";
import "./MetaTravelAdsCampaign.scss";
import MetaTravelAdsCampaignCreator from "../../features/meta-travel-ads-campaign/MetaTravelAdsCampaignCreator";
import { initialMonthlyBudget } from "./utils/initialState";
import PageTitles from "../../components/PageTitles";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { getUserToken } from "../../utils/functions/userToken";
import { createCampaign } from "features/meta-travel-ads-campaign/Utils/campaignCreator";
import { AttributionByStrategy } from "features/meta-travel-ads-campaign/constants/attributionSettingsOptions";
import {
  getSharedAudienceList,
  mapSharedAudienceListToOptions,
} from "features/meta-travel-ads-campaign/Utils/getSharedAudienceList";
import { APP_META_TRAVEL_ADS_API_ENDPOINT } from "../../constants/api_urls";
import useMetaOptions from "features/meta-travel-ads-campaign/Utils/useMetaOptions";
import { updateHotelId } from "store/slices/account/accountSlice";

export const initialRules = [{ id: 1, type: "INCLUDE" }, { id: 2, type: "EXCLUDE" }];

const MetaTravelAdsCampaignCreatorPage = () => {
  const token = getUserToken();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idhotel: urlHotelId } = useParams();

  const { hotel_id = [] } = useSelector((state) => state?.account);
  const account = useSelector((state) => state?.account?.account);

  // Sync hotel_id from URL param to Redux on mount/refresh
  useEffect(() => {
    if (urlHotelId && (!hotel_id.length || !hotel_id.includes(Number(urlHotelId)))) {
      dispatch(updateHotelId([Number(urlHotelId)]));
    }
  }, [urlHotelId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
  const [expandedPanel, setExpandedPanel] = useState("settings");
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [visitedPanels, setVisitedPanels] = useState(["settings"]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [biddingStrategy, setBiddingStrategy] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [campaignBudget, setCampaignBudget] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState(initialMonthlyBudget);
  const [dailyBudgetCap, setDailyBudgetCap] = useState(false);
  const [placementDetails, setPlacementDetails] = useState({
    placement_device: ["1", "2"],
    placement_platform: ["1", "2", "3", "4"],
  });
  const [clickThroughValue, setClickThroughValue] = useState(7);
  const [viewThroughValue, setViewThroughValue] = useState(1);

  // Auto-set attribution windows based on bidding strategy (Meta API restriction)
  useEffect(() => {
    const defaults = AttributionByStrategy[biddingStrategy];
    if (defaults) {
      setClickThroughValue(defaults.click_through);
      setViewThroughValue(defaults.view_through);
    }
  }, [biddingStrategy]);

  //Audiences
  const [ageRange, setAgeRange] = useState({ min: "", max: "" });
  const [selectedGender, setSelectedGender] = useState("ALL");
  const [rules, setRules] = useState(initialRules);
  const [isBookingWindowEnabled, setIsBookingWindowEnabled] = useState(false);
  const [bookingWindowRange, setBookingWindowRange] = useState({
    booking_window_start: null,
    booking_window_end: null,
  });
  const [selectedMetaAudience, setSelectedMetaAudience] = useState(null);
  const [selectedIncludeAudiences, setSelectedIncludeAudiences] = useState([]);
  const [selectedExcludeAudiences, setSelectedExcludeAudiences] = useState([]);
  const [metaAudienceOptions, setMetaAudienceOptions] = useState([]);
  const [isLoadingMetaAudienceOptions, setIsLoadingMetaAudienceOptions] = useState(false);

  const { isSharedAudienceEnabled, isMultiHotelEnabled } = useMetaOptions(hotel_id?.[0]);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingMetaAudienceOptions(true);
    getSharedAudienceList(hotel_id?.[0] ? { hotel_id: hotel_id[0] } : {})
      .then((res) => {
        if (!isMounted) return;
        if (res.success) {
          setMetaAudienceOptions(mapSharedAudienceListToOptions(res.data));
        } else {
          enqueueSnackbar(res.error, { variant: "error" });
          setMetaAudienceOptions([]);
        }
      })
      .finally(() => {
        if (isMounted) setIsLoadingMetaAudienceOptions(false);
      });
    return () => {
      isMounted = false;
    };
  }, [hotel_id, enqueueSnackbar]);
  //Ads Creative
  const [adsCreativeCards, setAdsCreativeCards] = useState([{ ad_card_id: 1 }]);
  const [selectedHotels, setSelectedHotels] = useState([]);

  // Pre-select the initial hotel so it's always part of selectedHotels
  useEffect(() => {
    if (hotel_id?.[0] && selectedHotels.length === 0) {
      setSelectedHotels([String(hotel_id[0])]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel_id]);
  const selectedAccount = useSelector((state) => state?.account?.account || {});
  const currencySymbol = selectedAccount["currency"] === "EUR" ? "€" : "$";

  const handleConfirmClose = () => {
    navigate(`/meta-travel-ads-campaign-manager/${account?.account_id}?hotelid=${hotel_id[0]}`);
  };

  const campaignSettingsPayload = {
    campaign_name: campaignName,
    bidding_strategy: {
      bidding_strategy_option: biddingStrategy,
      campaign_target_budget: biddingStrategy === "MAXIMIZE_CONVERSIONS" ? campaignBudget : null,
    },
    budget: {
      default_budget: targetValue,
      ...monthlyBudget,
      is_daily_budget: dailyBudgetCap,
    },
    placement_platform: placementDetails.placement_platform.map((platform) => platform.toUpperCase()),
    placement_device: placementDetails.placement_device.map((device) => device.toUpperCase()),
    point_of_sales: selectedCountries,
    campaign_lang: selectedLanguage?.map((lang) => lang.id_lang) || [],
    attr_click_through: Number(clickThroughValue),
    attr_view_through: Number(viewThroughValue),
  };

  const campaignAudiences = (() => {
    const baseRules = rules
      .filter(
        (rule) =>
          !initialRules.some(
            (initialRule) => JSON.stringify(initialRule) === JSON.stringify(rule),
          ),
      )
      .map((rule) => ({
        type: rule.type,
        source: rule.source || null,
        operator: rule.operator || null,
        days_from: Number(rule.daysRange?.days_from),
        days_to: Number(rule.daysRange?.days_to),
      }));

    if (selectedObjective === "META_AUDIENCE") {
      return {
        source_type: "SHARED",
        shared_audience_id: selectedMetaAudience
          ? Number(selectedMetaAudience)
          : null,
        shared_audience_ids: selectedIncludeAudiences.length > 0
          ? selectedIncludeAudiences.map(Number)
          : undefined,
        excluded_audience_ids: selectedExcludeAudiences.length > 0
          ? selectedExcludeAudiences.map(Number)
          : undefined,
      };
    }

    return {
      source_type: "NEW",
      shared_audience_id: null,
      audience_type: selectedObjective,
      age_min: Number(ageRange.min),
      age_max: Number(ageRange.max),
      gender: selectedGender,
      booking_window_start: Number(bookingWindowRange.booking_window_start),
      booking_window_end: Number(bookingWindowRange.booking_window_end),
      rules: baseRules,
      shared_audience_ids: selectedObjective === "PROSPECTING" && selectedIncludeAudiences.length > 0
        ? selectedIncludeAudiences.map(Number)
        : undefined,
    };
  })();

  const handleNext = async (currentPanel) => {
    if (currentPanel === "settings") {
      setExpandedPanel("audiences");
      setVisitedPanels((prev) => [...prev, "audiences"]);
    } else if (currentPanel === "audiences") {
      setExpandedPanel("adsCreative");
      setVisitedPanels((prev) => [...prev, "adsCreative"]);
    } else if (currentPanel === "adsCreative") {
      const response = await createCampaign({
        campaign_account_id: account.account_id,
        campaign_hotel_id: hotel_id[0],
        campaign_hotel_ids: selectedHotels.length > 0 ? selectedHotels : undefined,
        campaign_settings: campaignSettingsPayload,
        campaign_audience: campaignAudiences,
        campaign_ads_creative: adsCreativeCards.filter(card => {
          // Only include cards that have meaningful creative content
          if (!card || Object.keys(card).length <= 1) return false;
          if (!card.hotel_id || card.hotel_id === "null" || card.hotel_id === "undefined") return false;
          // Only include creatives for explicitly selected hotels
          const validHotels = selectedHotels.length > 0 ? selectedHotels.map(String) : [String(hotel_id[0])];
          if (!validHotels.includes(String(card.hotel_id))) return false;
          // Must have at least headline or primary_text filled by the user
          return card.headline?.assetContent || card.primary_text?.assetContent;
        }),
      });

      if (response.success) {
        enqueueSnackbar("Meta Hotel Ads Campaign created successfully!", { variant: "success" });
        navigate(`/meta-travel-ads-campaign-manager/${account.account_id}?hotelid=${hotel_id[0]}`);
      } else {
        const errorData = response?.error;
        if (errorData?.campaign_ads_creative && Object.keys(errorData.campaign_ads_creative).length > 0) {
          Object.keys(errorData.campaign_ads_creative).forEach((key) => {
            errorData.campaign_ads_creative[key].forEach((err) => {
              enqueueSnackbar(err.errorMessage, { variant: "error" });
            });
          });
        } else if (errorData?.errorMessage) {
          enqueueSnackbar(errorData.errorMessage, { variant: "error" });
        } else if (typeof errorData === "string") {
          enqueueSnackbar(errorData, { variant: "error" });
        } else {
          enqueueSnackbar("Failed to create campaign", { variant: "error" });
        }
      }
    }
  };

  return (
    <DefaultLayout
      page={"MetaTravel"}
      mainBoxSx={{
        display: "flex",
        minHeight: "95.5vh",
        overflow: "hidden",
      }}
      contentBoxSx={{
        backgroundColor: "#FAFAFA",
        flex: 1,
        paddingTop: 2,
        width: "calc(100vw - 180px)",
        overflow: "auto",
        minHeight: "100vh",
      }}
    >
      <PageTitles
        pageTitle="Meta Travel Ads"
        pageSubTitle="Campaign Creator"
        onClose={() => navigate(`/meta-travel-ads-campaign-manager/${account?.account_id}?hotelid=${hotel_id?.[0]}`)}
      />

      <Box className="metaTravelAdsContainer">
        <MetaTravelAdsCampaignCreator
          expandedPanel={expandedPanel}
          setExpandedPanel={setExpandedPanel}
          selectedObjective={selectedObjective}
          setSelectedObjective={setSelectedObjective}
          visitedPanels={visitedPanels}
          handleNext={handleNext}
          selectedCountries={selectedCountries}
          setSelectedCountries={setSelectedCountries}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          biddingStrategy={biddingStrategy}
          setBiddingStrategy={setBiddingStrategy}
          campaignName={campaignName}
          setCampaignName={setCampaignName}
          campaignBudget={campaignBudget}
          setCampaignBudget={setCampaignBudget}
          targetValue={targetValue}
          setTargetValue={setTargetValue}
          monthlyBudget={monthlyBudget}
          setMonthlyBudget={setMonthlyBudget}
          dailyBudgetCap={dailyBudgetCap}
          setDailyBudgetCap={setDailyBudgetCap}
          placementDetails={placementDetails}
          setPlacementDetails={setPlacementDetails}
          currencySymbol={currencySymbol}
          ageRange={ageRange}
          setAgeRange={setAgeRange}
          selectedGender={selectedGender}
          setSelectedGender={setSelectedGender}
          isBookingWindowEnabled={isBookingWindowEnabled}
          setIsBookingWindowEnabled={setIsBookingWindowEnabled}
          bookingWindowRange={bookingWindowRange}
          setBookingWindowRange={setBookingWindowRange}
          rules={rules}
          setRules={setRules}
          adsCreativeCards={adsCreativeCards}
          setAdsCreativeCards={setAdsCreativeCards}
          clickThroughValue={clickThroughValue}
          setClickThroughValue={setClickThroughValue}
          viewThroughValue={viewThroughValue}
          setViewThroughValue={setViewThroughValue}
          selectedMetaAudience={selectedMetaAudience}
          setSelectedMetaAudience={setSelectedMetaAudience}
          selectedIncludeAudiences={selectedIncludeAudiences}
          setSelectedIncludeAudiences={setSelectedIncludeAudiences}
          selectedExcludeAudiences={selectedExcludeAudiences}
          setSelectedExcludeAudiences={setSelectedExcludeAudiences}
          metaAudienceOptions={metaAudienceOptions}
          isLoadingMetaAudienceOptions={isLoadingMetaAudienceOptions}
          isSharedAudienceEnabled={isSharedAudienceEnabled}
          isMultiHotelEnabled={isMultiHotelEnabled}
          selectedHotels={selectedHotels}
          setSelectedHotels={setSelectedHotels}
        />
      </Box>
    </DefaultLayout>
  );
};

export default MetaTravelAdsCampaignCreatorPage;
